import { Line3, Object3D, Quaternion, Ray, Vector3, Group } from 'three';
import { LineDotted } from '@/Engine/Primitives/LineDotted';
import { WardrobeObject } from '@/Engine/Wardrobe/WardrobeObject';
import { Door } from '@/Engine/Door';
import { Window } from '@/Engine/Window';
import { rayIntersection, sides } from '@/Interfaces/common';
import { RackModule } from '@/Engine/Wardrobe/RackModule';
import { ShelfSet } from '@/Engine/Wardrobe/ShelfSet';
import { PerforatedPanel } from '@/Engine/Wardrobe/PerforatedPanel';
import { $ } from '@/Engine/stateInstance';
import { TextLine } from '@/Engine/Ruler/TextLine';
import { TextMesh } from '@/Engine/Ruler/TextMesh';
import { OBB } from 'three/examples/jsm/math/OBB';
import { useUIOnlyStore } from '@/Store/storeUI';
import { markCollisions } from '@/Engine/eventHandlers/lib/markCollisions';

type target = RackModule | ShelfSet | PerforatedPanel | Window | Door
export type selectionBoxTarget = target;
export class SelectionBoxForBox {
    model: Object3D;
    depth = 1;
    height = 1;
    width = 1;
    target: target | null;
    targetGroup: Array<RackModule> = [];
    side: sides | null = null;
    dockingTarget: any | null;
    dockingShelfTemplate: any | null;
    rulerUp: TextLine;
    rulerDown: TextLine;
    rulerLeft: TextLine;
    rulerRight: TextLine;
    rulerBack: TextLine;
    rulerFront: TextLine;
    obb = new OBB();
    upIntersection: rayIntersection | null;
    downIntersection: rayIntersection | null;
    leftIntersection: rayIntersection | null;
    rightIntersection: rayIntersection | null;
    backIntersection: rayIntersection | null;
    frontIntersection: rayIntersection | null;
    public rays = {
        up: new Ray(),
        down: new Ray(),
        left: new Ray(),
        right: new Ray(),
        back: new Ray(),
        front: new Ray(),
    };

    set visible(state: boolean) {
        this.model.visible = state;
        this.model.children.forEach((child)=>{
            child.visible = state;
        });
    }

    get visible() {
        return this.model.visible;
    }
    constructor() {
        const startPoint = new Vector3(1,1,1);
        const depth = 1;
        const height = 1;
        const width = 1;
        this.target = null;
        this.model = new Group();
        // this.model.visible = false;
        this.model.position.copy(startPoint);
        this.updateGeometry();
        this.rulerUp = new TextLine(new Line3());
        // this.rulerDown = new TextLine(new Line3());
        this.rulerLeft = new TextLine(new Line3());
        this.rulerRight = new TextLine(new Line3());
        this.rulerBack = new TextLine(new Line3());
        this.rulerFront = new TextLine(new Line3());
    }

    select(target: target) {
        $.selectionBox.unselect();
        if (!this.rulerUp) {
            this.rulerUp = new TextLine(new Line3());
            // this.rulerDown = new TextLine(new Line3());
            this.rulerLeft = new TextLine(new Line3());
            this.rulerRight = new TextLine(new Line3());
            this.rulerBack = new TextLine(new Line3());
            this.rulerFront = new TextLine(new Line3());
        }

        this.target = target;
        this.update();
    }

    updateIntersections() {
        if (!this.target) return;
        const excludeUuids = [];
        if (this.target.parent) {
            excludeUuids.push(this.target.parent.uuid);
        }

        let intersectObjects;
        intersectObjects = $.getAllObjectsWithExclude(excludeUuids);


        this.upIntersection = this.target.getLowerObject(this.target.rays.up, intersectObjects);
        this.downIntersection = this.target.getLowerObject(this.target.rays.down, intersectObjects);
        this.leftIntersection = this.target.getLowerObject(this.target.rays.left, intersectObjects);
        this.rightIntersection = this.target.getLowerObject(this.target.rays.right, intersectObjects);
        this.backIntersection = this.target.getLowerObject(this.target.rays.back, intersectObjects);
        this.frontIntersection = this.target.getLowerObject(this.target.rays.front, intersectObjects);
    }

    getLowerObject(ray: Ray, objects: Array<WardrobeObject>) {
        const out: rayIntersection = {
            distance: null,
            object: null,
            point: null,
        };

        objects.forEach((child)=> {
            const point = new Vector3();
            const intersect = child.obb.intersectRay(ray, point);
            if (!intersect) return null;
            const line =  new Line3(ray.origin, intersect);
            if (!out.distance || out.distance > line.distance()) {
                out.distance = line.distance();
                out.object = child;
                out.point = intersect;
            }
        });

        return out;
    }

    updateRuler() {
        if (!this.rulerUp) return;
        if (!this.target || !$.isRuler) {
            this.rulerUp.model.removeFromParent();
            // this.rulerDown.model.removeFromParent();
            this.rulerLeft.model.removeFromParent();
            this.rulerRight.model.removeFromParent();
            this.rulerFront.model.removeFromParent();
            this.rulerBack.model.removeFromParent();
            return;
        }


        const quaternion = new Quaternion();
        this.target.model.getWorldQuaternion(quaternion);

        if (this.upIntersection.object) {
            this.rulerUp.update(new Line3(this.target.rays.up.origin, this.upIntersection.point), quaternion);
            // this.rulerUp.model.removeFromParent();
            // this.rulerUp = new TextLine(new Line3(this.target.obb.center, upIntersection.point));
            $.app.scene.add(this.rulerUp.model);
            // this.model.add(this.rulerUp.model);
        } else {
            this.rulerUp.model.removeFromParent();
        }

        // if (this.downIntersection.object) {
        //     // this.rulerDown.update(new Line3(this.target.rays.down.origin, this.downIntersection.point), quaternion);
        //     // this.rulerDown.model.removeFromParent();
        //     // this.rulerDown = new TextLine(new Line3(this.target.rays.down.origin, downIntersection.point));
        //     // $.app.scene.add(this.rulerDown.model);
        // } else {
        //     this.rulerDown.model.removeFromParent();
        // }

        if (this.leftIntersection.object) {
            this.rulerLeft.update(new Line3(this.target.rays.left.origin, this.leftIntersection.point), quaternion);
            // this.rulerLeft.model.removeFromParent();
            // this.rulerLeft = new TextLine(new Line3(this.target.rays.left.origin, leftIntersection.point));
            $.app.scene.add(this.rulerLeft.model);
        } else {
            this.rulerLeft.model.removeFromParent();
        }

        if (this.rightIntersection.object) {
            this.rulerRight.update(new Line3(this.target.rays.right.origin, this.rightIntersection.point), quaternion);
            // this.rulerRight.model.removeFromParent();
            // this.rulerRight = new TextLine(new Line3(this.target.rays.right.origin, rightIntersection.point));
            $.app.scene.add(this.rulerRight.model);
        } else {
            this.rulerRight.model.removeFromParent();
        }

        if (this.backIntersection.object) {
            this.rulerBack.update(new Line3(this.target.rays.back.origin, this.backIntersection.point), quaternion);
            this.rulerBack.textMesh.model.rotateY(Math.PI / 2);
            // this.rulerRight.model.removeFromParent();
            // this.rulerRight = new TextLine(new Line3(this.target.rays.right.origin, rightIntersection.point));
            $.app.scene.add(this.rulerBack.model);
        } else {
            this.rulerBack.model.removeFromParent();
        }

        if (this.frontIntersection.object) {
            this.rulerFront.update(new Line3(this.target.rays.front.origin, this.frontIntersection.point), quaternion);
            this.rulerFront.textMesh.model.rotateY(Math.PI / 2);
            // this.rulerRight.model.removeFromParent();
            // this.rulerRight = new TextLine(new Line3(this.target.rays.right.origin, rightIntersection.point));
            $.app.scene.add(this.rulerFront.model);
        } else {
            this.rulerFront.model.removeFromParent();
        }

    }

    computeObb() {
        if (!this.target) return;
        this.obb.copy(this.target.obb);
    }

    update() {
        this.updateGeometry();
        this.updateIntersections();
        this.updateRuler();
    }

    unselect() {
        this.model.clear();
        this.target = null;
        this.updateRuler();
        useUIOnlyStore().unselect();
    }

    setPositionDelta(line: Line3, xOnly = false) {
        if (!this.target) return;
        const oldPosition = new Vector3().copy(this.target.position);

        const newPos = new Vector3(
            line.end.x,
            this.target.position.y,
            line.end.z,
        );

        if (newPos.distanceTo(this.target.position) < 0.001) return true;

        this.target.position.copy(newPos);
        this.target.computeObb();
        this.updateGeometry();
        this.updateIntersections();
        this.updateRuler();
        let collisions;
        if (!this.target) return;

        // TODO
        // collisions = $.checkCollision(this.target, this.side, []);
        // markCollisions();
        //
        // collisions = $.checkShelfCollisionWithWall(this.target, this.side);
        // if (this.target.rackModule && !collisions) {
        //     collisions = $.checkShelfCollisionWithWall(this.target.rackModule, this.side, []);
        // }
        let restriction = false;

        if (this.target.position.x - this.target.width/2 < 0) {
            this.target.position = new Vector3(
                this.target.width/2,
                this.target.position.y,
                this.target.position.z,
            );
            restriction = true;
        }

        if (this.target.position.x + this.target.width/2 > $.room.width) {
            this.target.position = new Vector3(
                $.room.width - this.target.width/2,
                this.target.position.y,
                this.target.position.z,
            );
            restriction = true;
        }

        const zCorrection = 0.015;
        if (this.target.position.z - this.target.depth/2 < zCorrection) {
            this.target.position = new Vector3(
                this.target.position.x,
                this.target.position.y,
                this.target.depth/2+zCorrection,
            );
            restriction = true;
        }

        if (this.target.position.z + this.target.depth/2 > $.room.depth) {
            this.target.position = new Vector3(
                this.target.position.x,
                this.target.position.y,
                $.room.depth - this.target.depth/2,
            );
            restriction = true;
        }

        markCollisions();

        $.moved = true;
        // console.log(collisions);
        if (restriction) {
            if (!this.target) return false;
            // this.target.position = oldPosition;
            this.updateGeometry();
            this.updateIntersections();
            this.updateRuler();
            return false;
        } else {
            return true;
        }
    }

    updateGeometry() {
        this.computeObb();
        this.model.clear();

        if (!this.target) return;
        // if (!this.target && !group) return;
        // $.app.scene.children
        //     .filter((item)=>item.name === 'temp')
        //     .forEach((item)=>item.removeFromParent());


        // const helper = new Mesh(new BoxGeometry(1,1,1,1,1,1), new MeshBasicMaterial({ wireframe: true }));
        const size = new Vector3();
        this.obb.getSize(size);
        const quaternion = new Quaternion();
        this.target.model.getWorldQuaternion(quaternion);

        const lines3 = [
            new Line3(new Vector3(0,0,0), new Vector3(0,size.y,0)),
            new Line3(new Vector3(0,size.y,0), new Vector3(0,size.y,size.z)),
            new Line3(new Vector3(0,0,0), new Vector3(0,0,size.z)),
            new Line3(new Vector3(0,0,size.z), new Vector3(0,size.y,size.z)),
            new Line3(new Vector3(0,0,0), new Vector3(size.x,0,0)),
            new Line3(new Vector3(size.x,0,0), new Vector3(size.x,size.y,0)),
            new Line3(new Vector3(size.x,0,0), new Vector3(size.x,0,size.z)),
            new Line3(new Vector3(size.x,0,size.z), new Vector3(size.x,size.y,size.z)),
            new Line3(new Vector3(size.x,size.y,0), new Vector3(size.x,size.y,size.z)),
            new Line3(new Vector3(0,0,size.z), new Vector3(size.x,0,size.z)),
            new Line3(new Vector3(0,size.y,size.z), new Vector3(size.x,size.y,size.z)),
            new Line3(new Vector3(0,size.y,0), new Vector3(size.x,size.y,0)),
        ];

        const addRuler = (line: Line3, shift: Vector3) => {
            const textY = new TextMesh(''+ Math.ceil(line.distance()*1000));
            const lineCenter:Vector3=new Vector3();
            line.getCenter(lineCenter);
            lineCenter.add(shift);
            textY.model.applyQuaternion(quaternion);
            textY.model.position.copy(lineCenter);

            this.model.add(textY.model);
        };
        // //
        // if (this.side === 'front') {
        //     addRuler(lines3[10], new Vector3(0,0.02, 0));
        //     addRuler(lines3[8], new Vector3(0.02,0, 0));
        //     addRuler(lines3[7], new Vector3(0.02,0, 0));
        // }
        //
        // if (this.side === 'left') {
        //     addRuler(lines3[8], new Vector3(0,0.02, 0));
        //     addRuler(lines3[11], new Vector3(0.02,0, 0));
        //     addRuler(lines3[5], new Vector3(0.02,0, 0));
        // }
        //
        // if (this.side === 'right') {
        //     addRuler(lines3[1], new Vector3(0,0.02, 0));
        //     addRuler(lines3[10], new Vector3(0.02,0, 0));
        //     addRuler(lines3[3], new Vector3(0.02,0, 0));
        // }
        //
        // if (this.side === 'back') {
        //     addRuler(lines3[11], new Vector3(0,0.02, 0));
        //     addRuler(lines3[1], new Vector3(0.02,0, 0));
        //     addRuler(lines3[0], new Vector3(0.02,0, 0));
        // }


        addRuler(lines3[10], new Vector3(0,0.02, 0));
        addRuler(lines3[8], new Vector3(0.02,0, 0));
        addRuler(lines3[7], new Vector3(0.02,0, 0));


        //
        lines3.forEach((line)=>this.model.add(new LineDotted(line.start, line.end).model));
        const startPosition = new Vector3();
        startPosition.copy(this.obb.center).sub(this.obb.halfSize);
        this.model.position.copy(startPosition);

        // lines.forEach((line)=>this.model.add(line.model));
        // this.visible = false;
    }

}

